import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  useCreateReferral,
  type PPRDocuments,
  type HospitalDocuments,
  type OtherDocuments,
  type DocumentsToSend,
  type AssessmentSchemes,
  type PaperAnamneseForms,
  type SchoolCompletionForms,
} from '@/hooks/useReferrals';
import { ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DynamicTextFields } from '@/components/ui/DynamicTextFields';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

export function NewReferral() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const create = useCreateReferral();

  const [referrerName, setReferrerName] = useState('');
  const [referrerType, setReferrerType] = useState<string>('');
  const [referrerReference, setReferrerReference] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cpr, setCpr] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  // New administrative fields
  const [secretaryName, setSecretaryName] = useState('');
  const [psychologistName, setPsychologistName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [caseNumber, setCaseNumber] = useState('');

  // PPR Documents
  const [pprDocs, setPprDocs] = useState<PPRDocuments>({});
  
  // Hospital Documents
  const [hospitalDocs, setHospitalDocs] = useState<HospitalDocuments>({});
  
  // Other Documents
  const [otherDocs, setOtherDocs] = useState<OtherDocuments>({});
  
  // Documents to Send
  const [docsToSend] = useState<DocumentsToSend>({});
  
  // Assessment Schemes
  const [assessmentSchemes, setAssessmentSchemes] = useState<AssessmentSchemes>({});

  // Paper Anamnese Forms
  const [paperAnamnese, setPaperAnamnese] = useState<PaperAnamneseForms>({});

  // School Completion Forms
  const [schoolCompletion, setSchoolCompletion] = useState<SchoolCompletionForms>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!activeClinicId) {
      setSubmitError(t('referrals.errors.no_clinic'));
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError(t('referrals.errors.name_required'));
      return;
    }

    if (cpr.trim().length > 0) {
      const digits = cpr.replace(/\D/g, '');
      if (digits.length !== 10) {
        setSubmitError(t('referrals.errors.cpr_format'));
        return;
      }
    }

    setSubmitting(true);
    try {
      const created = await create.mutateAsync({
        clinic_id: activeClinicId,
        referrer_name: referrerName.trim() || undefined,
        referrer_type: referrerType || undefined,
        referrer_reference: referrerReference.trim() || undefined,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        cpr: cpr.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        reason: reason.trim() || undefined,
        notes: notes.trim() || undefined,
        secretary_name: secretaryName.trim() || undefined,
        psychologist_name: psychologistName.trim() || undefined,
        id_number: idNumber.trim() || undefined,
        case_number: caseNumber.trim() || undefined,
        ppr_documents: pprDocs,
        hospital_documents: hospitalDocs,
        other_documents: otherDocs,
        documents_to_send: docsToSend,
        assessment_schemes: assessmentSchemes,
        paper_anamnese_forms: paperAnamnese,
        school_completion_forms: schoolCompletion,
      });
      toast.success(
        t('referrals.toast_created', { name: `${created.first_name} ${created.last_name}` }),
      );
      navigate(`/henvisninger/${created.id}`);
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'invalid-input') {
          setSubmitError(t('referrals.errors.invalid_input'));
        } else if (err.code === 'forbidden') {
          setSubmitError(t('referrals.errors.forbidden'));
        } else {
          setSubmitError(err.message);
        }
      } else {
        setSubmitError(t('referrals.errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="max-w-none px-6">
          <button
            type="button"
            onClick={() => navigate('/henvisninger')}
            className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('referrals.back_to_list')}
          </button>

          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:tracking-tight">
                {t('referrals.new_title')}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('referrals.new_subtitle')}
              </p>
            </div>
          </div>

          {submitError && (
            <div className="mt-6">
              <ErrorAlert message={submitError} />
            </div>
          )}

          <form
            onSubmit={onSubmit}
            className="mt-6 space-y-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark"
          >
            <Section title={t('referrals.section_referrer')}>
              <Grid>
                <Input
                  label={t('referrals.field_referrer_name')}
                  value={referrerName}
                  onChange={(e) => setReferrerName(e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('referrals.field_referrer_type')}
                  </label>
                  <select
                    value={referrerType}
                    onChange={(e) => setReferrerType(e.target.value)}
                    className="mt-1 block h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('referrals.referrer_type_none')}</option>
                    <option value="egen_laege">{t('referrals.referrer_type_egen_laege')}</option>
                    <option value="speciallaege">
                      {t('referrals.referrer_type_speciallaege')}
                    </option>
                    <option value="selvhenvist">
                      {t('referrals.referrer_type_selvhenvist')}
                    </option>
                    <option value="anden">{t('referrals.referrer_type_anden')}</option>
                  </select>
                </div>
                <Input
                  label={t('referrals.field_referrer_reference')}
                  value={referrerReference}
                  onChange={(e) => setReferrerReference(e.target.value)}
                  hint={t('referrals.field_referrer_reference_hint')}
                />
              </Grid>
            </Section>

            <Section title={t('referrals.section_patient')}>
              <Grid>
                <Input
                  label={t('referrals.field_first_name')}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
                <Input
                  label={t('referrals.field_last_name')}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
                <Input
                  label={t('referrals.field_cpr')}
                  value={cpr}
                  onChange={(e) => setCpr(e.target.value)}
                  placeholder="1234567890"
                  hint={t('referrals.field_cpr_hint')}
                />
                <Input
                  label={t('referrals.field_phone')}
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <Input
                  label={t('referrals.field_email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
            </Section>

            <Section title={t('referrals.section_details')}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('referrals.field_reason')}
                  </label>
                  <textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder={t('referrals.field_reason_placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('referrals.field_notes')}
                  </label>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder={t('referrals.field_notes_placeholder')}
                  />
                </div>
              </div>
            </Section>

            <Section title="Administrative">
              <Grid>
                <Input
                  label="Sekretær"
                  value={secretaryName}
                  onChange={(e) => setSecretaryName(e.target.value)}
                />
                <Input
                  label="Psykolog"
                  value={psychologistName}
                  onChange={(e) => setPsychologistName(e.target.value)}
                />
                <Input
                  label="ID #"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                />
                <Input
                  label="Sagsnr."
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value)}
                />
              </Grid>
            </Section>

            <div className="grid gap-6 lg:grid-cols-2">
              <Section title="Anmodning om dokumenter fra PPR">
                <div className="space-y-3">
                  <Input
                    label="PPR"
                    value={pprDocs.institution_name ?? ''}
                    onChange={(e) => setPprDocs({ ...pprDocs, institution_name: e.target.value })}
                    placeholder="Angiv by, evt. også afdeling eller kontaktperson"
                  />
                  <CheckboxField
                    label="WISC V forside eller computerudskrift"
                    checked={pprDocs.wisc_v_forside ?? false}
                    onChange={(checked) => setPprDocs({ ...pprDocs, wisc_v_forside: checked })}
                  />
                  <CheckboxField
                    label="WISC V rapport"
                    checked={pprDocs.wisc_v_rapport ?? false}
                    onChange={(checked) => setPprDocs({ ...pprDocs, wisc_v_rapport: checked })}
                  />
                  <CheckboxField
                    label="WPPSI-IV forside"
                    checked={pprDocs.wppsi_iv_forside ?? false}
                    onChange={(checked) => setPprDocs({ ...pprDocs, wppsi_iv_forside: checked })}
                  />
                  <CheckboxField
                    label="WPPSI-IV rapport"
                    checked={pprDocs.wppsi_iv_rapport ?? false}
                    onChange={(checked) => setPprDocs({ ...pprDocs, wppsi_iv_rapport: checked })}
                  />
                  <CheckboxField
                    label="Andet"
                    checked={pprDocs.andet ?? false}
                    onChange={(checked) => setPprDocs({ 
                      ...pprDocs, 
                      andet: checked,
                      // Initialize andet_items with one empty field when checked
                      ...(checked ? { andet_items: [''] } : {})
                    })}
                  />
                  {pprDocs.andet && (
                    <div className="ml-6 mt-2">
                      <DynamicTextFields
                        values={pprDocs.andet_items ?? []}
                        onChange={(items) => setPprDocs({ ...pprDocs, andet_items: items })}
                        placeholder="Beskriv hvad der ønskes..."
                      />
                    </div>
                  )}
                </div>
              </Section>

              <Section title="Anmodning om dokumenter fra Hospital">
                <div className="space-y-3">
                  <Input
                    label="Hospital"
                    value={hospitalDocs.institution_name ?? ''}
                    onChange={(e) => setHospitalDocs({ ...hospitalDocs, institution_name: e.target.value })}
                    placeholder="Angiv hvilket, afdeling og årstal"
                  />
                  <CheckboxField
                    label="Journaludskrift"
                    checked={hospitalDocs.journaludskrift ?? false}
                    onChange={(checked) =>
                      setHospitalDocs({ ...hospitalDocs, journaludskrift: checked })
                    }
                  />
                  
                  {/* Expandable Scannings-svar section */}
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={hospitalDocs.scannings_svar ?? false}
                        onChange={(e) => setHospitalDocs({ ...hospitalDocs, scannings_svar: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Scannings-svar</span>
                      <ChevronRight 
                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ease-in-out ${
                          hospitalDocs.scannings_svar ? 'rotate-90' : ''
                        }`}
                      />
                    </label>
                    
                    {/* Nested options with smooth transition */}
                    <div 
                      className={`
                        overflow-hidden transition-all duration-200 ease-in-out
                        ${hospitalDocs.scannings_svar ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'}
                      `}
                    >
                      <div className="ml-6 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-2">
                        <CheckboxField
                          label="MR"
                          checked={hospitalDocs.scannings_svar_mr ?? false}
                          onChange={(checked) =>
                            setHospitalDocs({ ...hospitalDocs, scannings_svar_mr: checked })
                          }
                        />
                        <CheckboxField
                          label="CT"
                          checked={hospitalDocs.scannings_svar_ct ?? false}
                          onChange={(checked) =>
                            setHospitalDocs({ ...hospitalDocs, scannings_svar_ct: checked })
                          }
                        />
                        <CheckboxField
                          label="Andet"
                          checked={hospitalDocs.scannings_svar_andet ?? false}
                          onChange={(checked) =>
                            setHospitalDocs({ 
                              ...hospitalDocs, 
                              scannings_svar_andet: checked,
                              // Initialize scannings_svar_andet_items with one empty field when checked
                              ...(checked ? { scannings_svar_andet_items: [''] } : {})
                            })
                          }
                        />
                        {hospitalDocs.scannings_svar_andet && (
                          <div className="mt-2 animate-in fade-in duration-200">
                            <DynamicTextFields
                              values={hospitalDocs.scannings_svar_andet_items ?? []}
                              onChange={(items) =>
                                setHospitalDocs({ ...hospitalDocs, scannings_svar_andet_items: items })
                              }
                              placeholder="Angiv hvem og hvad der ønskes, Husk årstal"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <CheckboxField
                    label="Andet"
                    checked={hospitalDocs.andet ?? false}
                    onChange={(checked) => setHospitalDocs({ 
                      ...hospitalDocs, 
                      andet: checked,
                      // Initialize andet_items with one empty field when checked
                      ...(checked ? { andet_items: [''] } : {})
                    })}
                  />
                  {hospitalDocs.andet && (
                    <div className="ml-6 mt-2">
                      <DynamicTextFields
                        values={hospitalDocs.andet_items ?? []}
                        onChange={(items) =>
                          setHospitalDocs({ ...hospitalDocs, andet_items: items })
                        }
                        placeholder="Beskriv hvad der ønskes..."
                      />
                    </div>
                  )}
                </div>
              </Section>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <Section title="Anmodning om dokumenter fra Andre">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Ønskede dokumenter
                  </label>
                  <textarea
                    rows={3}
                    value={otherDocs.requested_items ?? ''}
                    onChange={(e) =>
                      setOtherDocs({ ...otherDocs, requested_items: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder="Angiv hvem og hvad der ønskes, Husk årstal"
                  />
                </div>
              </Section>
            </div>

            {/* Forms sent with invitation wrapper */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Følgende medsendes indkaldelse
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  derudover sendes altid Anamnese, samtykkeerklæring hjem/skole/institution samt vejkort
                </p>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-6">
              <Section title="Papir Anamneseskema">
                <div className="space-y-2">
                  <CheckboxField
                    label="Småbørn"
                    checked={paperAnamnese.smaaboern ?? false}
                    onChange={(checked) => setPaperAnamnese({ ...paperAnamnese, smaaboern: checked })}
                  />
                  <CheckboxField
                    label="Småbørn genhenvisning"
                    checked={paperAnamnese.smaaboern_genhenvisning ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, smaaboern_genhenvisning: checked })
                    }
                  />
                  <CheckboxField
                    label="Skolebørn"
                    checked={paperAnamnese.skoleboern ?? false}
                    onChange={(checked) => setPaperAnamnese({ ...paperAnamnese, skoleboern: checked })}
                  />
                  <CheckboxField
                    label="Skolebørn genhenvisning"
                    checked={paperAnamnese.skoleboern_genhenvisning ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, skoleboern_genhenvisning: checked })
                    }
                  />
                  <CheckboxField
                    label="Unge 15+"
                    checked={paperAnamnese.unge_15_plus ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, unge_15_plus: checked })
                    }
                  />
                  <CheckboxField
                    label="Unge 15+ genhenvisning"
                    checked={paperAnamnese.unge_15_plus_genhenvisning ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, unge_15_plus_genhenvisning: checked })
                    }
                  />
                  <CheckboxField
                    label="Tillæg til anamneseskema (13-18 år)"
                    checked={paperAnamnese.tillaeg_til_anamnese_13_18 ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, tillaeg_til_anamnese_13_18: checked })
                    }
                  />
                  <CheckboxField
                    label="Nordisk spørgeskema (2-5 år)"
                    checked={paperAnamnese.nordisk_sporgeskema_2_5 ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, nordisk_sporgeskema_2_5: checked })
                    }
                  />
                  <CheckboxField
                    label="DUPAUL forældre // skole"
                    checked={paperAnamnese.dupaul_foraeldre_skole ?? false}
                    onChange={(checked) =>
                      setPaperAnamnese({ ...paperAnamnese, dupaul_foraeldre_skole: checked })
                    }
                  />
                </div>
              </Section>

              <Section title="Skema til udfyldelse af førskole/skole">
                <div className="space-y-2">
                  <CheckboxField
                    label="Førskole"
                    checked={schoolCompletion.førskole ?? false}
                    onChange={(checked) =>
                      setSchoolCompletion({ ...schoolCompletion, førskole: checked })
                    }
                  />
                  <CheckboxField
                    label="Indskoling"
                    checked={schoolCompletion.indskoling ?? false}
                    onChange={(checked) =>
                      setSchoolCompletion({ ...schoolCompletion, indskoling: checked })
                    }
                  />
                  <CheckboxField
                    label="Mellemtrin"
                    checked={schoolCompletion.mellemtrin ?? false}
                    onChange={(checked) =>
                      setSchoolCompletion({ ...schoolCompletion, mellemtrin: checked })
                    }
                  />
                  <CheckboxField
                    label="Udskoling"
                    checked={schoolCompletion.udskoling ?? false}
                    onChange={(checked) =>
                      setSchoolCompletion({ ...schoolCompletion, udskoling: checked })
                    }
                  />
                </div>
              </Section>
              </div>

              <Section title="Elektroniske skemaer (assessments)">
                <div className="space-y-2">
                  <div className="grid grid-cols-1 gap-2">
                    <CheckboxField
                      label="ABAS-3 førskole (forældre/pædagog)"
                      checked={assessmentSchemes.abas_3_førskole_foraeldre_paedagog ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({
                          ...assessmentSchemes,
                          abas_3_førskole_foraeldre_paedagog: checked,
                        })
                      }
                    />
                    <CheckboxField
                      label="ABAS-3 skole (forældre/lærer)"
                      checked={assessmentSchemes.abas_3_skole_foraeldre_laerer ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({
                          ...assessmentSchemes,
                          abas_3_skole_foraeldre_laerer: checked,
                        })
                      }
                    />
                    <CheckboxField
                      label="ABAS-3 voksen (selv/pårørende)"
                      checked={assessmentSchemes.abas_3_voksen_selv_paaroerende ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({
                          ...assessmentSchemes,
                          abas_3_voksen_selv_paaroerende: checked,
                        })
                      }
                    />
                    <CheckboxField
                      label="BRIEF-2 førskole (forældre/pædagog)"
                      checked={assessmentSchemes.brief_2_førskole_foraeldre_paedagog ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({
                          ...assessmentSchemes,
                          brief_2_førskole_foraeldre_paedagog: checked,
                        })
                      }
                    />
                    <CheckboxField
                      label="BRIEF-2 skole (forældre/lærer)"
                      checked={assessmentSchemes.brief_2_skole_foraeldre_laerer ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({
                          ...assessmentSchemes,
                          brief_2_skole_foraeldre_laerer: checked,
                        })
                      }
                    />
                    <CheckboxField
                      label="BRIEF-2 voksen (selv/pårørende)"
                      checked={assessmentSchemes.brief_2_voksen_selv_paaroerende ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({
                          ...assessmentSchemes,
                          brief_2_voksen_selv_paaroerende: checked,
                        })
                      }
                    />
                    <CheckboxField
                      label="BRIEF + selv"
                      checked={assessmentSchemes.brief_selv ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({ ...assessmentSchemes, brief_selv: checked })
                      }
                    />
                    <CheckboxField
                      label="BRIEF + SRS multi (besvarelse)"
                      checked={assessmentSchemes.brief_srs_multi_besvarelse ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({
                          ...assessmentSchemes,
                          brief_srs_multi_besvarelse: checked,
                        })
                      }
                    />
                    <CheckboxField
                      label="SRS førskole (forældre/lærer)"
                      checked={assessmentSchemes.srs_førskole_foraeldre_laerer ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({
                          ...assessmentSchemes,
                          srs_førskole_foraeldre_laerer: checked,
                        })
                      }
                    />
                    <CheckboxField
                      label="SRS skole (forældre/lærer)"
                      checked={assessmentSchemes.srs_skole_foraeldre_laerer ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({
                          ...assessmentSchemes,
                          srs_skole_foraeldre_laerer: checked,
                        })
                      }
                    />
                    <CheckboxField
                      label="VINELAND førskole"
                      checked={assessmentSchemes.vineland_førskole ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({ ...assessmentSchemes, vineland_førskole: checked })
                      }
                    />
                    <CheckboxField
                      label="VINELAND skole"
                      checked={assessmentSchemes.vineland_skole ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({ ...assessmentSchemes, vineland_skole: checked })
                      }
                    />
                    <CheckboxField
                      label="Andet"
                      checked={assessmentSchemes.andet ?? false}
                      onChange={(checked) =>
                        setAssessmentSchemes({ 
                          ...assessmentSchemes, 
                          andet: checked,
                          ...(checked ? { andet_items: [''] } : {})
                        })
                      }
                    />
                    {assessmentSchemes.andet && (
                      <div className="ml-6 mt-2">
                        <DynamicTextFields
                          values={assessmentSchemes.andet_items ?? []}
                          onChange={(items) =>
                            setAssessmentSchemes({ ...assessmentSchemes, andet_items: items })
                          }
                          placeholder="Angiv andre assessments..."
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Section>
            </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/henvisninger')}
                disabled={submitting}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={submitting || !activeClinicId}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('referrals.save_new')
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
      />
      <span className="text-sm text-gray-900 dark:text-white">{label}</span>
    </label>
  );
}
